import { useState, useEffect } from 'react';
import {
  fetchProviders,
  fetchModels,
  fetchConfigByCategory,
  fetchConfigByPromptId,
  applyConfig,
} from './llmConfigUtils';

const DEFAULT_SECTION = {
  id: 'primary',
  label: 'LLM Configuration',
  modelType: 'text',
  primary: true,
};

export const useLLMConfig = (category = null, name = null) => {
  const [llmProviders, setLlmProviders] = useState([]);
  const [sections, setSections] = useState([]);
  const [primarySectionId, setPrimarySectionId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLLMConfig = async () => {
      try {
        setLoading(true);
        const baseConfig = await fetchConfigByCategory(category, name);
        const baseDefinition = baseConfig?.definition || null;
        const basePromptId = baseConfig?.promptId || null;
        const configSections = Array.isArray(baseDefinition?.modelConfigSections)
          ? baseDefinition.modelConfigSections
          : [DEFAULT_SECTION];

        const sectionPromptIds = configSections.map((section) => section.promptId || basePromptId);
        const uniquePromptIds = [...new Set(sectionPromptIds.filter(Boolean))];
        const configsByPromptId = new Map();
        if (baseConfig?.promptId) {
          configsByPromptId.set(baseConfig.promptId, baseConfig);
        }

        const additionalPromptIds = uniquePromptIds.filter((id) => !configsByPromptId.has(id));
        if (additionalPromptIds.length > 0) {
          const configs = await Promise.all(additionalPromptIds.map((id) => fetchConfigByPromptId(id)));
          configs.forEach((config) => {
            if (config?.promptId) {
              configsByPromptId.set(config.promptId, config);
            }
          });
        }

        const modelTypes = [...new Set(configSections.map((section) => section.modelType || 'text'))];
        const providers = await fetchProviders();
        const modelsByTypeEntries = await Promise.all(
          modelTypes.map(async (type) => [type, await fetchModels(type)])
        );
        const modelsByType = Object.fromEntries(modelsByTypeEntries);
        setLlmProviders(providers);

        const primarySection = configSections.find((section) => section.primary) || configSections[0] || null;
        setPrimarySectionId(primarySection?.id || null);

        const nextSections = configSections.map((section) => {
          const promptId = section.promptId || basePromptId;
          const config = promptId ? configsByPromptId.get(promptId) : null;
          const modelType = section.modelType || 'text';
          const models = modelsByType[modelType] || [];
          const defaults = {
            selectedProvider: '',
            selectedModel: '',
            llmTemperature: 0.7,
            llmMaxTokens: 4000,
            fallbackProvider: '',
            fallbackModel: '',
          };

          const state = { ...defaults };
          applyConfig({
            config,
            providers,
            models,
            setSelectedProvider: (value) => {
              state.selectedProvider = value;
            },
            setSelectedModel: (value) => {
              state.selectedModel = value;
            },
            setLlmTemperature: (value) => {
              state.llmTemperature = value;
            },
            setLlmMaxTokens: (value) => {
              state.llmMaxTokens = value;
            },
            setFallbackProvider: (value) => {
              state.fallbackProvider = value;
            },
            setFallbackModel: (value) => {
              state.fallbackModel = value;
            },
          });

          return {
            id: section.id,
            label: section.label || 'LLM Configuration',
            modelType,
            promptId,
            models,
            selectedProvider: state.selectedProvider,
            selectedModel: state.selectedModel,
            llmTemperature: state.llmTemperature,
            llmMaxTokens: state.llmMaxTokens,
            fallbackProvider: state.fallbackProvider,
            fallbackModel: state.fallbackModel,
          };
        });

        setSections(nextSections);
      } catch (err) {
        console.error('Error fetching LLM config:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLLMConfig();
  }, [category, name]);

  const updateSection = (id, updates) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === id ? { ...section, ...updates } : section
      )
    );
  };

  const primarySection = sections.find((section) => section.id === primarySectionId) || sections[0] || null;

  return {
    llmProviders,
    sections,
    primarySection,
    loading,
    updateSection,
  };
};
